import type { SiteData } from '@/data/types';
import { resolveImagePath } from '@/utils/imageResolver';

interface FooterProps {
  siteData: SiteData;
}

export function Footer({ siteData }: FooterProps) {
  return (
    <footer className="bg-red-600 text-white relative">
      {/* Decorative white line at top */}
      <div className="absolute top-0 left-0 w-full h-4">
        <svg viewBox="0 0 1200 60" className="w-full h-full" preserveAspectRatio="none">
          <path
            d="M0,30 C200,10 400,50 600,30 C800,10 1000,50 1200,30 L1200,60 L0,60 Z"
            fill="white"
          />
        </svg>
      </div>

      <div className="pt-16 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            
            {/* Left Section - Branding & Certifications */}
            <div className="lg:col-span-2">
              {/* Company Name */}
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Atlas Automotive Repair
              </h2>

              {/* Social Media Icons */}
              <div className="flex gap-4 mb-8">
                <a
                  href={siteData.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-200 transition-colors"
                  aria-label="Follow us on Instagram"
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718 0c-1.297 0-2.448-.49-3.323-1.297-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297z"/>
                  </svg>
                </a>
                
                <a
                  href={siteData.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-200 transition-colors"
                  aria-label="Follow us on Facebook"
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                
                <a
                  href={siteData.social.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-200 transition-colors"
                  aria-label="Find us on Google"
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c6.627 0 12 5.373 12 12s-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0zm4.5 7.5h-9v9h9v-9z"/>
                  </svg>
                </a>
              </div>

              {/* Certification Logos */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
                {/* BBB Accredited Business */}
                <div className="bg-white p-3 rounded">
                  <img
                    src={resolveImagePath('/images/bbb-badge.png')}
                    alt="BBB Accredited Business"
                    className="w-full h-auto max-h-16 object-contain"
                  />
                </div>

                {/* TechNet Professional */}
                <div className="bg-white p-3 rounded">
                  <img
                    src={resolveImagePath('/images/technet-logo.png')}
                    alt="TechNet Professional"
                    className="w-full h-auto max-h-16 object-contain"
                  />
                </div>

                {/* Jasper Engines & Transmissions */}
                <div className="bg-red-500 p-3 rounded flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-white font-bold text-xs leading-tight">
                      JASPER<br />
                      ENGINES &<br />
                      TRANSMISSIONS
                    </div>
                    <div className="text-white text-xs mt-1">
                      3 Years / 100,000 Miles<br />
                      Parts & Labor<br />
                      <span className="font-semibold">Nationwide Warranty!</span>
                    </div>
                  </div>
                </div>

                {/* Oklahoma Route 66 Association */}
                <div className="bg-white p-3 rounded">
                  <img
                    src={resolveImagePath('/images/route66-logo.png')}
                    alt="Oklahoma Route 66 Association"
                    className="w-full h-auto max-h-16 object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Right Section - Contact & Location */}
            <div className="lg:pl-8">
              <div className="space-y-6">
                {/* Contact Section */}
                <div>
                  <h4 className="text-xl font-bold mb-3">Contact</h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <a 
                        href={`mailto:${siteData.email}`}
                        className="underline hover:no-underline"
                      >
                        Send us an Email
                      </a>
                    </p>
                    <p>
                      <a 
                        href={`tel:${siteData.phone.replace(/[^\d]/g, '')}`}
                        className="underline hover:no-underline"
                      >
                        Give us a Call
                      </a>
                    </p>
                    <p>
                      <a 
                        href={`sms:${siteData.phone.replace(/[^\d]/g, '')}`}
                        className="underline hover:no-underline"
                      >
                        Send us a Text
                      </a>
                    </p>
                  </div>
                </div>

                {/* Location & Hours Section */}
                <div>
                  <h4 className="text-xl font-bold mb-3">Location & Hours</h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <a 
                        href="/location"
                        className="underline hover:no-underline"
                      >
                        4625 E 11th St<br />
                        Tulsa, OK 74112
                      </a>
                    </p>
                    <p>
                      Mon-Fri 9-5<br />
                      Drop-off 24hr
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-red-400 text-center">
            <p className="text-sm text-white">
              Hakuna Matata 2025
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}